// src/prototype/help/helpContent.ts
// ============================================================
// HELP CONTENT REGISTRY
// 
// Central registry for all contextual help content in the EA prototype.
// Each key maps to a title, body text, and optional learn more link.
//
// Phase 1: 10 initial help keys covering key friction points
// Phase 2: 32 additional keys for comprehensive coverage
// Phase 3: Weight settings keys for category/measure configuration
// ============================================================

export type HelpKey =
  // Phase 1 Keys
  | "personalization_strength"
  | "organization_profile"
  | "data_intake_surveys"
  | "document_upload"
  | "advanced_personalization"
  | "mark_as_finalist"
  | "why_this_was_surfaced"
  | "scoring_rationale"
  | "roi_model"
  | "approval_readiness"
  // Phase 2 Keys - Cross-cutting
  | "finalist_vs_opportunity"
  | "personalized_to_your_org"
  | "flagged_items"
  // Phase 2 Keys - Opportunities List
  | "finalist_count_badge"
  | "effort_badges"
  | "time_to_value"
  | "est_value_range"
  // Phase 2 Keys - Opportunities Expanded
  | "scenario_analysis"
  | "base_case"
  | "confidence_level"
  | "payback_period"
  | "key_assumptions"
  | "assumption_types_baseline"
  | "assumption_types_impact"
  | "assumption_types_financial"
  | "assumption_types_timeline"
  | "flag_assumption"
  | "primary_value_driver"
  | "primary_risk"
  | "use_case_profile_help"
  // Phase 2 Keys - Validation Page
  | "execution_portfolio"
  | "combined_annual_value"
  | "execution_snapshot"
  | "resources_panel"
  | "approval_materials"
  | "exec_one_pager"
  | "cfo_roi_summary"
  | "board_rationale_slides"
  | "assumptions_and_risks_doc"
  | "reorder_priority"
  | "export_roi_csv_help"
  | "champion_assistance_help"
  // Journey Bar Keys
  | "journey_personalize"
  | "journey_evaluate_shortlist"
  | "journey_approval_execute"
  // Phase 3 Keys - Weight Settings
  | "category_weights"
  | "measure_weights";

export interface HelpContent {
  title: string;
  body: string;
  learnMoreHref?: string;
}

export const helpContent: Record<HelpKey, HelpContent> = {
  // ============================================================
  // PHASE 1 KEYS
  // ============================================================
  personalization_strength: {
    title: "Personalization Strength",
    body: "A summary of how much organization-specific context DVA has to tailor rankings and ROI assumptions. Adding surveys and documents improves relevance and defensibility."
  },
  organization_profile: {
    title: "Organization Profile",
    body: "These details shape scale, constraints, and assumptions used throughout scoring and ROI modeling. More complete profiles produce more defensible recommendations."
  },
  data_intake_surveys: {
    title: "Data Intake Surveys",
    body: "Surveys capture priorities, capabilities, and readiness signals that influence ranking logic and ROI assumptions. You can complete them progressively—partial inputs still help."
  },
  document_upload: {
    title: "Document Upload",
    body: "Documents provide institutional context (priorities, constraints, metrics) that strengthens scoring and executive defensibility. Uploading 2–3 strategic documents materially improves results."
  },
  advanced_personalization: {
    title: "Advanced Personalization",
    body: "Optional controls to fine-tune how DVA evaluates and ranks opportunities for your organization. Recommended for advanced users or analytics teams."
  },
  mark_as_finalist: {
    title: "Mark as Finalist",
    body: "Choose the initiatives you want to take forward into approval-ready materials. Finalists become your execution shortlist for leadership review."
  },
  why_this_was_surfaced: {
    title: "Why This Was Surfaced",
    body: "Shows the specific signals from your inputs that caused this opportunity to rank highly. Use this to confirm relevance and defend the recommendation."
  },
  scoring_rationale: {
    title: "Scoring Rationale",
    body: "A transparent breakdown of how this use case was scored across key dimensions. Useful for explaining 'why this ranked' to stakeholders."
  },
  roi_model: {
    title: "ROI Model",
    body: "Detailed financial assumptions and scenarios used to estimate impact and payback. Use this when finance teams want to validate or stress-test the numbers."
  },
  approval_readiness: {
    title: "Approval Readiness",
    body: "A summary of how defensible this initiative is across alignment, readiness, feasibility, and financial confidence. Use it to anticipate objections before leadership review."
  },

  // ============================================================
  // PHASE 2 KEYS - Cross-cutting Concepts (ChatGPT content)
  // ============================================================
  finalist_vs_opportunity: {
    title: "Finalist vs Opportunity",
    body: "Opportunities are ranked options to consider. Finalists are the 1–3 initiatives you've chosen to take forward into approval-ready materials and execution planning."
  },
  personalized_to_your_org: {
    title: "Personalized to your organization",
    body: "Rankings and assumptions reflect your profile, surveys, and documents—not generic benchmarks. More inputs increase relevance and defensibility."
  },
  flagged_items: {
    title: "Flagged items",
    body: "Flags capture where an assumption or input may not match your reality. They help you document uncertainty and strengthen the approval narrative."
  },

  // ============================================================
  // PHASE 2 KEYS - Opportunities List View (ChatGPT content)
  // ============================================================
  finalist_count_badge: {
    title: "Finalists selected",
    body: "Shows how many initiatives you've chosen to advance. You can proceed once you've selected at least one finalist."
  },
  effort_badges: {
    title: "Effort level",
    body: "A quick estimate of implementation complexity and change effort. Use it to balance impact with what you can realistically execute."
  },
  time_to_value: {
    title: "Time to value",
    body: "Estimated time to deliver measurable outcomes after starting implementation. Shorter time-to-value typically increases approval probability."
  },
  est_value_range: {
    title: "Estimated value range",
    body: "A directional annual impact range based on your context and benchmark assumptions. Use the ROI Model to review the drivers behind it."
  },

  // ============================================================
  // PHASE 2 KEYS - Opportunities Expanded View (ChatGPT content)
  // ============================================================
  scenario_analysis: {
    title: "Scenario analysis",
    body: "Conservative, Base, and Aggressive scenarios show how outcomes change under different assumptions. Use scenarios to pressure-test defensibility with finance."
  },
  base_case: {
    title: "Base case",
    body: "The most likely scenario using default assumptions and benchmarks. Use this as the primary reference for comparison unless you've flagged key inputs."
  },
  confidence_level: {
    title: "Confidence",
    body: "A signal of how reliable the estimate is given your inputs and available benchmarks. Higher confidence generally means fewer unknowns and smoother approval."
  },
  payback_period: {
    title: "Payback period",
    body: "How long it takes for expected benefits to exceed costs. CFOs often use payback as a quick filter for prioritization."
  },
  key_assumptions: {
    title: "Key assumptions",
    body: "The critical inputs driving this ROI estimate—what must be true for results to materialize. Flag assumptions that feel unrealistic to improve defensibility."
  },
  assumption_types_baseline: {
    title: "Baseline assumption",
    body: "Your current-state metric used as the starting point (e.g., current no-show rate). Baselines anchor the size of the opportunity."
  },
  assumption_types_impact: {
    title: "Impact assumption",
    body: "The expected change driven by the initiative (e.g., % reduction in no-shows). Impact assumptions typically drive most variance in ROI."
  },
  assumption_types_financial: {
    title: "Financial assumption",
    body: "Unit economics and financial drivers (e.g., revenue per encounter, cost per event). Finance teams often validate these quickly."
  },
  assumption_types_timeline: {
    title: "Timeline assumption",
    body: "How long implementation and ramp-up are expected to take. Longer timelines usually reduce near-term ROI and weaken approval urgency."
  },
  flag_assumption: {
    title: "Flag an assumption",
    body: "Use flags to indicate an assumption is too high, too low, missing, or incorrect. Flags help you document what needs validation before approval."
  },
  primary_value_driver: {
    title: "Primary value driver",
    body: "The main mechanism producing value for this initiative (e.g., avoided cost, recovered revenue). Use it to explain 'where the money comes from.'"
  },
  primary_risk: {
    title: "Primary risk",
    body: "The most likely obstacle to realizing value (e.g., adoption, data availability, change capacity). Use it to proactively address leadership concerns."
  },
  use_case_profile_help: {
    title: "Use case profile",
    body: "A concise overview of what the initiative is, why it matters, and what it typically requires. Use it to align stakeholders quickly."
  },

  // ============================================================
  // PHASE 2 KEYS - Validation/Activate Page (ChatGPT content)
  // ============================================================
  execution_portfolio: {
    title: "Execution portfolio",
    body: "Your selected finalists ordered for leadership review. Reordering helps reflect political reality, sequencing, and execution capacity."
  },
  combined_annual_value: {
    title: "Combined annual value",
    body: "The aggregated annual value range across your selected finalists. Use it as a headline metric for leadership attention—not as a budget guarantee."
  },
  execution_snapshot: {
    title: "Execution snapshot",
    body: "A quick summary of ROI range, payback, confidence, and timeline for this finalist. Use it to orient leadership before sharing the full materials."
  },
  resources_panel: {
    title: "Resources",
    body: "Approval-ready artifacts and reference links for this finalist. Use these to reduce prep time and move from decision to execution faster."
  },
  approval_materials: {
    title: "Approval materials",
    body: "Pre-built documents designed for executive and finance review. They translate analytics into defensible narratives and ready-to-share deliverables."
  },
  exec_one_pager: {
    title: "Executive One-Pager",
    body: "A leadership-friendly summary of the initiative, expected impact, and why it matters now. Best for initial alignment conversations."
  },
  cfo_roi_summary: {
    title: "CFO ROI Summary",
    body: "Finance-facing ROI detail with assumptions and scenarios. Use it to accelerate CFO review and reduce back-and-forth."
  },
  board_rationale_slides: {
    title: "Board Rationale Slides",
    body: "A presentation-ready rationale aligned to governance expectations. Use it to explain strategic alignment, risk posture, and expected outcomes."
  },
  assumptions_and_risks_doc: {
    title: "Assumptions & Risks",
    body: "A defensibility appendix that documents key assumptions, flags, and primary risks. Use it to preempt questions and build trust."
  },
  reorder_priority: {
    title: "Priority order",
    body: "Reordering lets you reflect sequencing realities—dependencies, resourcing, and leadership priorities. The order guides how you present finalists."
  },
  export_roi_csv_help: {
    title: "Export ROI CSV",
    body: "A finance-friendly dataset of assumptions, scenarios, and outputs for handoff or independent validation. Export reflects your current selections and flags."
  },
  champion_assistance_help: {
    title: "Champion Assistance",
    body: "Messaging support for leadership conversations—talk tracks, objections, and summaries. It helps you explain decisions without changing the underlying numbers."
  },

  // ============================================================
  // JOURNEY BAR KEYS
  // ============================================================
  journey_personalize: {
    title: "Personalize",
    body: "Add your organization's context so rankings and ROI assumptions reflect your reality. More inputs improve relevance and defensibility."
  },
  journey_evaluate_shortlist: {
    title: "Evaluate & Shortlist",
    body: "Review ranked opportunities and choose 1–3 finalists to advance. Use 'Why surfaced' and the ROI model to confirm what's defensible."
  },
  journey_approval_execute: {
    title: "Get Approval & Execute",
    body: "Download approval-ready materials for your finalists and prepare leadership conversations. Reorder finalists to match execution reality."
  },

  // ============================================================
  // PHASE 3 KEYS - Weight Settings
  // ============================================================
  category_weights: {
    title: "Category Weights",
    body: "Category weights determine how much each scoring dimension influences opportunity rankings. Set your preferred percentages, then click \"Normalize to 100%\" to ensure they total correctly. Higher weight = more influence on final rankings."
  },
  measure_weights: {
    title: "Measure Weights",
    body: "Each category contains multiple measures. Measure weights determine importance within each category. Set your percentages for each measure, then normalize each category to 100%. This allows fine-grained control over scoring priorities."
  }
};